import {
  type FormatterPreset,
  type FormatterValueType,
  formatFormatterChain,
  parseFormatterChain,
  validateFormatterChain,
} from "@imminently/decisively-core";
import clsx from "clsx";
import React from "react";
import {
  NONE_FORMATTER,
  formatterStepOptions,
  nextFormatterFor,
  previewFormatters,
  withCurrentFormatter,
} from "../../../utils/formatterMenus";
import Button from "../../common/Button/Button";
import Icon from "../../common/Icon/Icon";
import Select from "../../form/Select/Select";
import TextInput from "../../form/TextInput/TextInput";
import styles from "./FormatterChainEditor.module.scss";

/**
 * Edits a formatter chain (`{{ key | a | b }}`'s `a | b`) as one select per
 * formatter. None on a row removes it, + appends the next unused option, and the
 * code toggle swaps the rows for the chain as typed text, for formatters the
 * menus don't offer. Menus come from `formatterMenus` (decisively-core's
 * registry), so any control that configures a formatter can use this, not only
 * the attribute picker.
 */
export interface FormatterChainEditorProps {
  /** Menu for the first formatter (a value type's set, including None). Later ones also get the text formatters. */
  options: FormatterPreset[];
  /** Formatters applied in order. Empty for the raw value. */
  formatters: string[];
  onChange: (formatters: string[]) => void;
  /** A value each menu option previews its output on. Without one, options show no preview. */
  exampleValue?: string;
  /** The example value's type, so the preview formats it like the renderer would. */
  valueType?: FormatterValueType;
  className?: string;
}

export const FormatterChainEditor = ({
  options,
  formatters,
  onChange,
  exampleValue,
  valueType,
  className,
}: FormatterChainEditorProps): React.ReactElement => {
  const baseOptions = options.length ? options : [NONE_FORMATTER];
  // A single None row when there are no formatters.
  const formatterRows = formatters.length ? formatters : [NONE_FORMATTER.formatter];
  const optionsForRow = (index: number) =>
    withCurrentFormatter(formatterStepOptions(baseOptions, index), formatterRows[index]);
  const nextFormatter = nextFormatterFor(baseOptions, formatters);

  const setFormatterAt = (index: number, next: string) => {
    if (next === NONE_FORMATTER.formatter) {
      onChange(formatters.filter((_, position) => position !== index));
    } else {
      onChange(formatterRows.map((current, position) => (position === index ? next : current)));
    }
  };
  const addFormatter = () => {
    if (nextFormatter) onChange([...formatters, nextFormatter.formatter]);
  };

  // Raw: the whole chain as typed text (`currency AUD | uppercase`), for formatters
  // the menus don't offer. Only a valid chain is passed up, so a half-typed one
  // never reaches the template; leaving raw drops whatever is still invalid.
  const [raw, setRaw] = React.useState(false);
  const [rawChain, setRawChain] = React.useState("");
  const rawChainError = raw ? validateFormatterChain(rawChain) : undefined;
  const rawChainErrorId = React.useId();
  const toggleRaw = () => {
    if (!raw) setRawChain(formatFormatterChain(formatters) ?? "");
    setRaw(!raw);
  };
  const changeRawChain = (next: string) => {
    setRawChain(next);
    if (!validateFormatterChain(next)) onChange(parseFormatterChain(next));
  };
  // Follow outside changes (e.g. re-pointing at another attribute) while the text is valid.
  const committedChain = formatFormatterChain(formatters) ?? "";
  React.useEffect(() => {
    setRawChain((current) =>
      validateFormatterChain(current) || (formatFormatterChain(parseFormatterChain(current)) ?? "") === committedChain
        ? current
        : committedChain,
    );
  }, [committedChain]);

  // Each option shows what it would output after the formatters before it in the chain.
  const previewFor = (index: number, option: string) =>
    exampleValue === undefined
      ? undefined
      : previewFormatters(
          exampleValue,
          [...formatters.slice(0, index), ...(option === NONE_FORMATTER.formatter ? [] : [option])],
          valueType,
        );

  return (
    <div
      className={clsx(styles.editor, className)}
      data-deci-formatter-chain="true"
    >
      <div className={styles.header}>
        <span className={styles.label}>{formatters.length > 1 ? "Formatters" : "Formatter"}</span>
        <span className={styles.actions}>
          <Button
            icon
            size="sm"
            // One label for both states; aria-pressed says whether it's on.
            label="Custom formatters"
            selected={raw}
            aria-pressed={raw}
            onClick={toggleRaw}
          >
            <Icon name="code-xml" />
          </Button>
          <Button
            icon
            size="sm"
            label="Add formatter"
            disabled={raw || !nextFormatter}
            onClick={addFormatter}
          >
            <Icon name="add" />
          </Button>
        </span>
      </div>
      {raw ? (
        <>
          <TextInput
            value={rawChain}
            onValueChange={changeRawChain}
            aria-label="Formatters"
            aria-invalid={rawChainError ? true : undefined}
            aria-describedby={rawChainError ? rawChainErrorId : undefined}
            error={Boolean(rawChainError)}
            placeholder="e.g. currency AUD"
            spellCheck={false}
            autoComplete="off"
            fullWidth
          />
          {rawChainError ? (
            <span
              id={rawChainErrorId}
              className={styles.error}
            >
              {rawChainError}
            </span>
          ) : null}
        </>
      ) : (
        formatterRows.map((value, index) => (
          <div
            // Formatters can repeat in a chain, so the position is the identity.
            key={index}
            className={styles.row}
          >
            <Select
              value={value}
              onValueChange={(next) => setFormatterAt(index, next ?? NONE_FORMATTER.formatter)}
              options={optionsForRow(index).map((option) => ({ value: option.formatter, label: option.label }))}
              aria-label={index === 0 ? "Formatter" : `Formatter ${index + 1}`}
              searchable={false}
              fullWidth
              renderMenuItem={(itemProps) => (
                <Select.MenuItem {...itemProps}>
                  <span className={styles.option}>
                    <span>{itemProps.data.label}</span>
                    <span className={styles.example}>{previewFor(index, itemProps.data.value)}</span>
                  </span>
                </Select.MenuItem>
              )}
            />
            {index > 0 ? (
              <Button
                icon
                size="sm"
                label="Remove formatter"
                onClick={() => setFormatterAt(index, NONE_FORMATTER.formatter)}
              >
                <Icon name="close" />
              </Button>
            ) : null}
          </div>
        ))
      )}
    </div>
  );
};

export default FormatterChainEditor;
